import { useState } from "react";
import { Download, Share2, Plus, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePWAInstall } from "@/hooks/usePWAInstall";

// Two-mode install nudger:
//
//   - Android/Chrome/Edge: native install prompt via `beforeinstallprompt`.
//     A single-line bar at the bottom of the viewport with an Install
//     button that opens the OS dialog.
//
//   - iOS Safari: no programmatic install API exists. We detect this and
//     swap to an instruction sheet showing the share-icon → "Zum
//     Home-Bildschirm" steps. Crucial for iOS users because Web Push only
//     works after the PWA is installed via this route.
//
// Banner is dismissible and self-quiets for 2 weeks (see hook). Once the
// app is installed, the banner never renders again on that device.

export function InstallBanner() {
  const pwa = usePWAInstall();
  const [showIosSheet, setShowIosSheet] = useState(false);

  if (!pwa.canPrompt) return null;

  const onClick = async () => {
    if (pwa.platform === "ios-safari") {
      setShowIosSheet(true);
      return;
    }
    const r = await pwa.install();
    if (r === "accepted") {
      // appinstalled event will hide the banner via isStandalone flip
    } else if (r === "dismissed") {
      // user said "not now" in the OS dialog — quiet ourselves too
      pwa.dismiss();
    }
  };

  return (
    <>
      {/* Floating, dismissible bar. Sits ABOVE the bottom nav (which is
          z-40, fixed). We use sticky-bottom inside its own fixed wrapper at
          z-30 so taps on the bar don't bleed through to nav buttons. */}
      <div className="fixed bottom-20 inset-x-0 z-30 px-3 pb-2 pointer-events-none">
        <div className="max-w-md mx-auto bg-card text-card-foreground border-2 border-accent rounded-lg shadow-pop p-3 flex items-center gap-3 pointer-events-auto animate-fade-in">
          <div className="bg-accent text-accent-foreground rounded-md p-2 shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold leading-tight">App installieren</div>
            <div className="text-[11px] text-muted-foreground leading-tight">
              {pwa.platform === "ios-safari"
                ? 'Auf iOS via Safari → Teilen → „Zum Home-Bildschirm".'
                : "Schneller Zugriff & Push-Benachrichtigungen direkt vom Startbildschirm."}
            </div>
          </div>
          <Button
            size="sm"
            onClick={onClick}
            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold whitespace-nowrap"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {pwa.platform === "ios-safari" ? "Anleitung" : "Installieren"}
          </Button>
          <button
            onClick={pwa.dismiss}
            aria-label="Banner ausblenden"
            className="text-muted-foreground hover:text-foreground p-1 tap"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* iOS instruction sheet */}
      <Dialog open={showIosSheet} onOpenChange={setShowIosSheet}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">📲 Auf dem iPhone installieren</DialogTitle>
            <DialogDescription>
              iOS unterstützt PWA-Installationen nur über Safari. So geht&apos;s in 3 Schritten:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3 items-start">
              <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center text-sm">
                1
              </span>
              <div className="pt-0.5">
                Diese Seite in <b>Safari</b> öffnen (nicht Chrome / Firefox auf iOS).
              </div>
            </li>
            <li className="flex gap-3 items-start">
              <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center text-sm">
                2
              </span>
              <div className="pt-0.5 flex items-center gap-1.5 flex-wrap">
                Unten auf <Share2 className="h-4 w-4 inline" /> <b>Teilen</b> tippen.
              </div>
            </li>
            <li className="flex gap-3 items-start">
              <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center text-sm">
                3
              </span>
              <div className="pt-0.5 flex items-center gap-1.5 flex-wrap">
                <Plus className="h-4 w-4 inline" /> <b>„Zum Home-Bildschirm"</b> wählen → <b>Hinzufügen</b>.
              </div>
            </li>
          </ol>
          <div className="rounded-md bg-accent/10 border border-accent/30 p-3 text-[12px]">
            💡 <b>Wichtig:</b> Push-Benachrichtigungen funktionieren auf iOS nur, nachdem du die App so
            installiert hast und sie über das Symbol auf dem Home-Bildschirm öffnest.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
